/**
 * Performance Utilities for React Components
 *
 * Efficient comparison functions for React.memo and performance optimization
 * to replace expensive JSON.stringify operations.
 */

/**
 * Shallow comparison function for objects
 * More efficient than JSON.stringify for React props comparison
 */
export function shallowCompare<T extends Record<string, unknown>>(obj1: T, obj2: T): boolean {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (obj1[key] !== obj2[key]) {
            return false;
        }
    }

    return true;
}

/**
 * Deep comparison function for complex objects (more efficient than JSON.stringify)
 * Uses recursive approach with depth limit to prevent infinite recursion
 */
export function deepCompare<T>(obj1: T, obj2: T, maxDepth = 3, currentDepth = 0): boolean {
    // Strict equality check
    if (obj1 === obj2) return true;

    // Type check
    if (typeof obj1 !== typeof obj2) return false;

    // Null/undefined check
    if (obj1 == null || obj2 == null) return obj1 === obj2;

    // Depth limit reached, fall back to shallow comparison
    if (currentDepth >= maxDepth) {
        return obj1 === obj2;
    }

    // Array comparison
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) return false;
        return obj1.every((item, index) => deepCompare(item, obj2[index], maxDepth, currentDepth + 1));
    }

    // Object comparison
    if (typeof obj1 === 'object' && typeof obj2 === 'object') {
        const keys1 = Object.keys(obj1 as Record<string, unknown>);
        const keys2 = Object.keys(obj2 as Record<string, unknown>);

        if (keys1.length !== keys2.length) return false;

        return keys1.every(key => deepCompare((obj1 as Record<string, unknown>)[key], (obj2 as Record<string, unknown>)[key], maxDepth, currentDepth + 1));
    }

    return false;
}

/**
 * Optimized comparison specifically for form values
 * Handles nested form structure efficiently
 */
export function compareFormValues(values1: Record<string, unknown>, values2: Record<string, unknown>): boolean {
    // Use deep comparison with limited depth for form values
    return deepCompare(values1, values2, 2);
}
